import { BusinessModel } from 'enums/businessModel';
import { SupportedDailyWeeklyRentalTypes } from 'enums/supportedRentalTypes';
import { Translate } from 'next-translate';
import { toast } from 'react-toastify';
import {
  VehicleBasedDailyWeeklyDemandState,
  VehicleBasedDemandState,
} from 'types/demand.type';
import { date, number, object, ref, ValidationError } from 'yup';

type VehicleBasedValidationSchema = (
  state: VehicleBasedDemandState,
  minDuration: number,
  maxDuration: number,
  fullCapacity: number,
  diningCapacity: number,
  t: Translate,
  showError?: boolean
) => Promise<boolean>;

const vehicleBasedValidationSchemaFactory = ({
  minDuration,
  maxDuration,
  capacity,
  variant,
}: {
  minDuration: number;
  maxDuration?: number;
  capacity: number;
  variant: BusinessModel;
}) => {
  const now = new Date();
  const duration = number()
    .required('vehicle:priceWidget.form.errors.durationRequired')
    .min(minDuration, 'vehicle:priceWidget.form.errors.durationMin');
  return object({
    harbor: number().required('vehicle:priceWidget.form.errors.harborRequired'),
    numberOfPeople: number()
      .required('vehicle:priceWidget.form.errors.numberOfPeopleRequired')
      .min(1, 'vehicle:priceWidget.form.errors.numberOfPeopleMin')
      .max(capacity, 'vehicle:priceWidget.form.errors.numberOfPeopleMax'),
    ...(variant === BusinessModel.HOURLY
      ? {
          date: date().required('vehicle:priceWidget.form.errors.dateRequired'),
          duration: !maxDuration
            ? duration
            : duration.max(
                maxDuration,
                'vehicle:priceWidget.form.errors.durationMax'
              ),
        }
      : {
          startDate: date()
            .required('vehicle:priceWidget.form.errors.dateRequired')
            .min(now, 'vehicle:priceWidget.form.errors.dateMin')
            .max(ref('endDate'), 'vehicle:priceWidget.form.errors.startDateMax')
            .notOneOf([ref('endDate')]),

          endDate: date()
            .required('vehicle:priceWidget.form.errors.dateRequired')
            .min(ref('startDate'), 'vehicle:priceWidget.form.errors.endDateMin')
            .notOneOf([ref('startDate')]),
        }),
  });
};

export const vehicleBasedValidation: VehicleBasedValidationSchema = async (
  state,
  minDuration,
  maxDuration,
  fullCapacity,
  diningCapacity,
  t,
  showError = false
) => {
  const capacity = state.selectedMeal !== null ? diningCapacity : fullCapacity;
  const validationSchema = vehicleBasedValidationSchemaFactory({
    minDuration,
    maxDuration,
    capacity,
    variant: BusinessModel.HOURLY,
  });

  try {
    await validationSchema.validate(state, {
      abortEarly: false,
    });

    return true;
  } catch (error) {
    if (showError && error instanceof ValidationError) {
      error.inner.forEach((e: ValidationError) => {
        toast.error(t(e.message), { toastId: `demandError-${e.name}` });
      });
    }
    return false;
  }
};

type VehicleBasedDWValidationSchema = (
  state: VehicleBasedDailyWeeklyDemandState,
  minDuration: number,
  cruisingCapacity: number,
  overnightCapacity: number,
  t: Translate,
  showError?: boolean
) => Promise<boolean>;

export const vehicleBasedDWValidation: VehicleBasedDWValidationSchema = async (
  state,
  minDuration,
  cruisingCapacity,
  overnightCapacity,
  t,
  showError = false
) => {
  const capacity =
    state.demandVariant === SupportedDailyWeeklyRentalTypes.HALF_DAY
      ? cruisingCapacity
      : overnightCapacity;
  const validationSchema = vehicleBasedValidationSchemaFactory({
    minDuration,
    capacity,
    variant: BusinessModel.DAILY_WEEKLY,
  });

  if (state.startDate?.getTime() === state.endDate?.getTime()) {
    return false;
  }

  try {
    await validationSchema.validate(state, {
      abortEarly: false,
    });

    return true;
  } catch (error) {
    if (showError && error instanceof ValidationError) {
      error.inner.forEach((e: ValidationError) => {
        toast.error(t(e.message), { toastId: `demandError-${e.name}` });
      });
    }
    return false;
  }
};
